import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, LinearProgress, Link, TextField, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  ArrowBack,
  CameraAltOutlined,
  Check,
  Close,
  ErrorOutline,
  OpenInNew,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationApiService, ShopifyInstallUrls } from '../../services/integrationApi';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useUnsavedChangesSource } from '../appUpdate';
import { tokens } from '../../theme/tokens';
import {
  CUSTOM_APP_SCREENSHOTS,
  CUSTOM_APP_STEP_COUNT,
  CUSTOM_APP_STEP_IDS,
  CustomAppStepId,
  CustomAppValues,
  checkInstallLink,
  credentialsEntered,
  installLinkForOtherApp,
  resolveStoredDone,
  resolveStoredStep,
  stepGateOpen,
} from './customAppWizardModel';

const DEV_DASHBOARD_URL = 'https://dev.shopify.com/dashboard';
const COPIED_MS = 1600;
const DRAFT_SAVE_DELAY_MS = 500;
export const CUSTOM_APP_DRAFTS_QUERY_KEY = ['shopify-custom-app-drafts'];

type ScopeGroup = 'inventory' | 'orders' | 'discounts' | 'markets';

/** The wizard's place and values, kept by the installer while it stays open, secret included. */
export interface CustomAppWizardSnapshot extends CustomAppValues {
  step: CustomAppStepId;
  done: CustomAppStepId[];
}

interface Props {
  /** The store's myshopify subdomain. */
  shop: string;
  installUrls: ShopifyInstallUrls | null;
  /** Optional scope groups the store has on; the catalog scopes are always listed. */
  scopeGroups: ScopeGroup[];
  /** Where to pick up from in this installer session. Without one, the saved draft is read. */
  resume: CustomAppWizardSnapshot | null;
  /** An error from the installer, such as a failed save for later. */
  error: string | null;
  savingForLater: boolean;
  /** Back arrow: the installer's connection options. */
  onBack: (snapshot: CustomAppWizardSnapshot) => void;
  onClose: () => void;
  /** Step 1's link: back to the options with Admin API token chosen. */
  onUseToken: (snapshot: CustomAppWizardSnapshot) => void;
  /** Resolves true once the app is saved, which deletes the draft on the server. */
  onSaveForLater: (values: CustomAppValues) => Promise<boolean>;
  /** Step 8's primary button: back to the installer's Permissions step, values filled in. */
  onFinish: (snapshot: CustomAppWizardSnapshot) => void;
}

const LabelText: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
  <Typography
    component={htmlFor ? 'label' : 'span'}
    htmlFor={htmlFor}
    sx={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, mb: 0.75 }}
  >
    {children}
  </Typography>
);

interface CopyFieldProps {
  label: string;
  value: string;
  onFailed: () => void;
}

const CopyField: React.FC<CopyFieldProps> = ({ label, value, onFailed }) => {
  const { t } = useAppTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      onFailed();
    }
  };
  return (
    <Box>
      <LabelText>{label}</LabelText>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          border: '1px solid',
          borderColor: tokens.line.default,
          borderRadius: `${tokens.radius.control}px`,
          bgcolor: tokens.surface.sunken,
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            px: 1.5,
            py: 1.25,
            fontFamily: tokens.font.mono,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: tokens.ink.primary,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Typography>
        <Button
          onClick={copy}
          aria-label={`${t('integrations.customAppWizard.copy')}: ${label}`}
          sx={{
            flex: '0 0 auto',
            px: 1.5,
            fontSize: 12.5,
            fontWeight: 600,
            borderRadius: 0,
            borderLeft: '1px solid',
            borderColor: tokens.line.default,
            bgcolor: tokens.surface.card,
            color: copied ? tokens.signal.positive.ink : tokens.accent.main,
            '&:hover': { bgcolor: tokens.surface.tint },
          }}
        >
          {t(copied ? 'integrations.customAppWizard.copied' : 'integrations.customAppWizard.copy')}
        </Button>
      </Box>
    </Box>
  );
};

/** A checkbox as Shopify should show it. Read-only: the merchant sets it over there. */
const ExpectedCheckbox: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => {
  const { t } = useAppTranslation();
  const state = t(checked ? 'integrations.customAppWizard.checked' : 'integrations.customAppWizard.unchecked');
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.375,
        border: '1px solid',
        borderColor: tokens.line.default,
        borderRadius: `${tokens.radius.control}px`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          flex: '0 0 auto',
          display: 'grid',
          placeItems: 'center',
          width: 18,
          height: 18,
          boxSizing: 'border-box',
          borderRadius: '4px',
          border: '1.5px solid',
          borderColor: checked ? tokens.accent.main : tokens.ink.disabled,
          bgcolor: checked ? tokens.accent.main : tokens.surface.card,
          color: tokens.ink.inverse,
        }}
      >
        {checked && <Check sx={{ fontSize: 14 }} />}
      </Box>
      <Typography sx={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>
        {label}
      </Typography>
      <Typography sx={{ flex: '0 0 auto', fontSize: 12, fontWeight: 600, color: checked ? tokens.accent.hover : tokens.ink.muted }}>
        {state}
      </Typography>
    </Box>
  );
};

const noteSx = { alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.5 };

const inputSx = { fontFamily: tokens.font.mono, fontSize: 13 };

/**
 * Walks a store through creating its Shopify custom app one step at a time, with each value to copy
 * beside the step that uses it and each value to paste asked for at the step that creates it. Where
 * it stands is saved per store on the server (`shopifyCustomAppDrafts`), never the client secret.
 * The scope list comes from the API (`src/utils/shopifyScopes.ts`); nothing here spells one out.
 */
const ShopifyCustomAppWizard: React.FC<Props> = ({
  shop,
  installUrls,
  scopeGroups,
  resume,
  error,
  savingForLater,
  onBack,
  onClose,
  onUseToken,
  onSaveForLater,
  onFinish,
}) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const domain = `${shop}.myshopify.com`;
  const copyKey = (path: string) => `integrations.customAppWizard.${path}`;

  const [step, setStep] = useState<CustomAppStepId>(resume?.step ?? CUSTOM_APP_STEP_IDS[0]);
  const [done, setDone] = useState<CustomAppStepId[]>(resume?.done ?? []);
  const [clientId, setClientId] = useState(resume?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState(resume?.clientSecret ?? '');
  const [installLink, setInstallLink] = useState(resume?.installLink ?? '');
  const [copyFailed, setCopyFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [hydrated, setHydrated] = useState(resume !== null);

  // A forced refresh would lose the secret, the one value that is never saved. The installer covers
  // it once this has handed the secret back.
  useUnsavedChangesSource({ dirty: clientSecret.length > 0, discard: () => setClientSecret('') });

  const draftsQuery = useQuery({
    queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY,
    queryFn: () => integrationApiService.getShopifyCustomAppDrafts(),
    enabled: resume === null,
    refetchOnMount: 'always',
    retry: false,
  });
  const draftsSettled = draftsQuery.isFetchedAfterMount || draftsQuery.isError;

  // Coming back opens the last step the merchant was on, once.
  useEffect(() => {
    if (hydrated || !draftsSettled) return;
    const draft = draftsQuery.data?.find((item) => item.shopId === shop);
    if (draft) {
      setStep(resolveStoredStep(draft.step));
      setDone(resolveStoredDone(draft.done));
      setClientId(draft.clientId);
      setInstallLink(draft.installLink);
    }
    setHydrated(true);
  }, [hydrated, draftsSettled, draftsQuery.data, shop]);

  const scopesQuery = useQuery({
    queryKey: ['shopify-custom-app-scopes'],
    queryFn: () => integrationApiService.getShopifyCustomAppScopes(),
    staleTime: Infinity,
    retry: false,
  });
  const scopes = scopesQuery.data;
  const scopeText = scopes
    ? [...scopes.core, ...scopeGroups.flatMap((group) => scopes.groups[group] || []), ...(scopes.redirect || [])].join(',')
    : '';

  // Saved shortly after each change, and at once on leaving, so nothing waits on the delay.
  const draft = { step, done, clientId: clientId.trim(), installLink: installLink.trim() };
  const draftKey = JSON.stringify(draft);
  const savedKey = useRef<string | null>(null);
  const latestDraft = useRef({ key: draftKey, draft });
  latestDraft.current = { key: draftKey, draft };

  const draftWritesHeld = useRef(false);
  const draftWrite = useRef<Promise<void>>(Promise.resolve());

  const saveDraft = useCallback(() => {
    const { key, draft: current } = latestDraft.current;
    if (draftWritesHeld.current || savedKey.current === key) return draftWrite.current;
    savedKey.current = key;
    draftWrite.current = integrationApiService
      .saveShopifyCustomAppDraft(shop, current)
      .then(() => setSaveFailed(false))
      .catch(() => {
        // Never equal to a real key, so the next change or leaving tries again.
        savedKey.current = '';
        setSaveFailed(true);
      });
    return draftWrite.current;
  }, [shop]);

  // Saving the app deletes the draft, so no draft write may land after it and bring the draft back:
  // hold new ones, let one in flight finish first, and let go only if the save failed.
  const saveForLater = async () => {
    draftWritesHeld.current = true;
    await draftWrite.current;
    const saved = await onSaveForLater(values);
    if (!saved) draftWritesHeld.current = false;
  };

  useEffect(() => {
    if (!hydrated) return undefined;
    // What it opened with is already saved; a store that only looks writes nothing.
    if (savedKey.current === null) {
      savedKey.current = latestDraft.current.key;
      return undefined;
    }
    if (savedKey.current === draftKey) return undefined;
    const timer = window.setTimeout(() => void saveDraft(), DRAFT_SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [hydrated, draftKey, saveDraft]);

  const flushRef = useRef(saveDraft);
  flushRef.current = saveDraft;
  const hydratedRef = useRef(hydrated);
  hydratedRef.current = hydrated;
  useEffect(
    () => () => {
      const saved = hydratedRef.current ? flushRef.current() : Promise.resolve();
      void saved.finally(() => queryClient.invalidateQueries({ queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY }));
    },
    [queryClient],
  );

  const mainRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const firstStepRender = useRef(true);
  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false;
      return;
    }
    mainRef.current?.scrollTo?.({ top: 0 });
    titleRef.current?.focus();
  }, [step]);

  const values: CustomAppValues = { clientId, clientSecret, installLink };
  const snapshot = (): CustomAppWizardSnapshot => ({ ...values, step, done });
  const stepIndex = CUSTOM_APP_STEP_IDS.indexOf(step);
  const last = stepIndex === CUSTOM_APP_STEP_COUNT - 1;
  const canNext = hydrated && stepGateOpen(step, values, domain);
  const linkCheck = checkInstallLink(installLink, domain);
  const linkOk = linkCheck.status === 'ok';
  const otherApp = linkOk && installLinkForOtherApp(installLink, clientId);
  const stepCopy = (part: string, options?: Record<string, unknown>) =>
    t(copyKey(`steps.${step}.${part}`), { domain, ...options });

  const onNext = () => {
    if (!canNext) return;
    const nextDone = done.includes(step) ? done : CUSTOM_APP_STEP_IDS.filter((id) => id === step || done.includes(id));
    setDone(nextDone);
    if (last) {
      onFinish({ ...values, step, done: nextDone });
      return;
    }
    setStep(CUSTOM_APP_STEP_IDS[stepIndex + 1]);
  };

  const linkHelper =
    linkCheck.status === 'invalid'
      ? t(copyKey('link.invalid'))
      : linkCheck.status === 'other_store'
        ? t(copyKey('link.otherStore'), { host: linkCheck.host, domain })
        : linkOk
          ? t(copyKey('link.ok'))
          : t(copyKey('link.help'));
  const linkHelperColor = linkOk ? tokens.signal.positive.ink : undefined;

  const copyProps = { onFailed: () => setCopyFailed(true) };

  const actions = (
    <>
      {step === 'open' && (
        <>
          <Button
            variant="outlined"
            href={DEV_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNew fontSize="small" />}
            sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
          >
            {stepCopy('link')}
          </Button>
          <Alert severity="info" sx={noteSx}>
            {stepCopy('tokenNote')}{' '}
            <Link component="button" type="button" onClick={() => onUseToken(snapshot())} sx={{ display: 'inline', font: 'inherit', fontWeight: 600, textAlign: 'left', verticalAlign: 'baseline' }}>
              {stepCopy('tokenLink')}
            </Link>
          </Alert>
        </>
      )}
      {step === 'urls' &&
        (installUrls ? (
          <>
            <CopyField label={stepCopy('appUrl')} value={installUrls.appUrl} {...copyProps} />
            <CopyField label={stepCopy('redirectUrl')} value={installUrls.redirectUrl} {...copyProps} />
          </>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: tokens.signal.caution.ink }}>{t(copyKey('loadFailed'))}</Typography>
        ))}
      {step === 'install' && (
        <>
          <ExpectedCheckbox label={stepCopy('embed')} checked={false} />
          <ExpectedCheckbox label={stepCopy('legacy')} checked />
        </>
      )}
      {step === 'scopes' &&
        (scopes ? (
          <CopyField label={stepCopy('field')} value={scopeText} {...copyProps} />
        ) : scopesQuery.isError ? (
          <Typography sx={{ fontSize: 12.5, color: tokens.signal.caution.ink }}>{t(copyKey('loadFailed'))}</Typography>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t(copyKey('loading'))}</Typography>
        ))}
      {step === 'release' && (
        <Alert severity="warning" sx={noteSx}>
          {stepCopy('note')}
        </Alert>
      )}
      {step === 'distribute' && (
        <>
          <CopyField label={stepCopy('domainField')} value={domain} {...copyProps} />
          <Alert severity="warning" sx={noteSx}>
            {stepCopy('note')}
          </Alert>
          <Box>
            <LabelText htmlFor="custom-app-install-link">{stepCopy('linkField')}</LabelText>
            <TextField
              id="custom-app-install-link"
              value={installLink}
              onChange={(event) => setInstallLink(event.target.value)}
              placeholder="https://admin.shopify.com/…"
              autoComplete="off"
              fullWidth
              error={linkCheck.status === 'invalid' || linkCheck.status === 'other_store'}
              helperText={linkHelper}
              inputProps={{ spellCheck: false }}
              InputProps={{ sx: { ...inputSx, ...(linkOk ? { boxShadow: `0 0 0 3px ${tokens.accent.tint}` } : {}) } }}
              FormHelperTextProps={{ sx: { mx: 0, color: linkHelperColor } }}
            />
          </Box>
        </>
      )}
      {step === 'credentials' && (
        <>
          <Box>
            <LabelText htmlFor="custom-app-client-id">{stepCopy('clientId')}</LabelText>
            <TextField
              id="custom-app-client-id"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              autoComplete="off"
              fullWidth
              error={otherApp}
              helperText={otherApp ? stepCopy('otherApp') : undefined}
              inputProps={{ spellCheck: false }}
              InputProps={{ sx: inputSx }}
              FormHelperTextProps={{ sx: { mx: 0 } }}
            />
          </Box>
          <Box>
            <LabelText htmlFor="custom-app-client-secret">{stepCopy('clientSecret')}</LabelText>
            <TextField
              id="custom-app-client-secret"
              type="password"
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
              autoComplete="off"
              fullWidth
              InputProps={{ sx: inputSx }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 12,
              color: linkOk ? tokens.signal.positive.ink : tokens.signal.caution.ink,
            }}
          >
            {linkOk ? <Check sx={{ fontSize: 14 }} /> : <ErrorOutline sx={{ fontSize: 14 }} />}
            <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
              {stepCopy(linkOk ? 'linkSaved' : 'linkMissing')}
            </Typography>
          </Box>
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={() => void saveForLater()}
              disabled={!credentialsEntered(values) || savingForLater}
              sx={{ ml: -1 }}
            >
              {t('integrations.customAppResume.saveForLater')}
            </Button>
          </Box>
        </>
      )}
      {copyFailed && (
        <Typography role="alert" sx={{ fontSize: 12.5, color: tokens.signal.caution.ink }}>
          {t(copyKey('copyFailed'))}
        </Typography>
      )}
    </>
  );

  const screenshot = CUSTOM_APP_SCREENSHOTS[step];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 3 }, py: 2.25 }}>
        <IconButton
          aria-label={t(copyKey('back'))}
          title={t(copyKey('back'))}
          size="small"
          onClick={() => onBack(snapshot())}
          sx={{ ml: -0.75, flex: '0 0 auto' }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box
          sx={{
            flex: '0 0 auto',
            width: 38,
            height: 38,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${tokens.radius.control}px`,
            bgcolor: tokens.signal.positive.tint,
          }}
        >
          {/* The bag is the left end of the wordmark file, so it is cropped to the bag. */}
          <Box sx={{ width: 20, height: 24, overflow: 'hidden' }}>
            <Box component="img" src="/logos/platforms/shopify.svg" alt="" sx={{ height: 24, width: 'auto', maxWidth: 'none', display: 'block' }} />
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16.5, fontWeight: 700, color: tokens.ink.primary, lineHeight: 1.25 }}>
            {t(copyKey('title'))}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>
            {t(copyKey('status'), { domain, done: done.length, total: CUSTOM_APP_STEP_COUNT })}
          </Typography>
        </Box>
        <IconButton aria-label={t(copyKey('close'))} title={t(copyKey('close'))} size="small" onClick={onClose} sx={{ flex: '0 0 auto' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(done.length / CUSTOM_APP_STEP_COUNT) * 100}
        aria-label={t(copyKey('progressLabel'))}
        sx={{ height: 3, flex: '0 0 auto', bgcolor: tokens.line.hairline, borderRadius: 0, '& .MuiLinearProgress-bar': { bgcolor: tokens.accent.main, borderRadius: 0 } }}
      />

      <Box
        ref={mainRef}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)' },
          alignContent: { xs: 'start', md: 'stretch' },
        }}
      >
        <Box
          component="nav"
          aria-label={t(copyKey('railTitle'))}
          sx={{
            order: { xs: 2, md: 1 },
            px: 1.5,
            py: 2.25,
            bgcolor: tokens.surface.sunken,
            borderTop: { xs: `1px solid ${tokens.line.hairline}`, md: 'none' },
            borderRight: { xs: 'none', md: `1px solid ${tokens.line.hairline}` },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          <Typography variant="overline" sx={{ px: 1.25, pb: 1, color: tokens.ink.faint }}>
            {t(copyKey('railTitle'))}
          </Typography>
          {CUSTOM_APP_STEP_IDS.map((id, index) => {
            const isDone = done.includes(id);
            const current = id === step;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                aria-current={current ? 'step' : undefined}
                onClick={() => setStep(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  px: 1.25,
                  py: 1,
                  border: 'none',
                  borderRadius: `${tokens.radius.small}px`,
                  bgcolor: current ? tokens.surface.card : 'transparent',
                  textAlign: 'left',
                  font: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: current ? tokens.surface.card : tokens.surface.tint },
                  '&:focus-visible': { outline: `2px solid ${tokens.accent.main}`, outlineOffset: 1 },
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    display: 'grid',
                    placeItems: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: '1.5px solid',
                    borderColor: isDone || current ? tokens.accent.main : tokens.ink.disabled,
                    bgcolor: isDone ? tokens.accent.main : tokens.surface.card,
                    color: isDone ? tokens.ink.inverse : current ? tokens.accent.main : tokens.ink.faint,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {isDone ? <Check sx={{ fontSize: 13 }} /> : index + 1}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: current ? 700 : 500,
                    color: current ? tokens.ink.primary : isDone ? tokens.ink.body : tokens.ink.muted,
                  }}
                >
                  {t(copyKey(`steps.${id}.short`))}
                  {isDone && (
                    <Box component="span" sx={visuallyHidden}>
                      {` (${t(copyKey('stepDone'))})`}
                    </Box>
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            order: { xs: 1, md: 2 },
            minWidth: 0,
            minHeight: { md: 560 },
            px: { xs: 2, sm: 3 },
            pt: 2.75,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.accent.main }}>
              {t(copyKey('stepOf'), { step: stepIndex + 1, total: CUSTOM_APP_STEP_COUNT })}
            </Typography>
            <Typography
              ref={titleRef}
              component="h3"
              tabIndex={-1}
              sx={{ fontSize: 19, fontWeight: 700, color: tokens.ink.primary, lineHeight: 1.3, outline: 'none' }}
            >
              {stepCopy('title')}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: tokens.ink.bodyAlt, lineHeight: 1.55, maxWidth: 620 }}>
              {stepCopy('desc')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 10',
                  borderRadius: `${tokens.radius.card}px`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: tokens.line.default,
                  bgcolor: tokens.surface.sunken,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {screenshot ? (
                  <Box
                    component="img"
                    src={screenshot}
                    alt={t(copyKey('screenshotAlt'), { title: stepCopy('title') })}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, fontSize: 12.5, color: tokens.ink.faint }}>
                    <CameraAltOutlined sx={{ fontSize: 16 }} />
                    {t(copyKey('screenshotPending'))}
                  </Typography>
                )}
              </Box>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.875, fontSize: 12.5, color: tokens.ink.muted }}>
                <CameraAltOutlined sx={{ flex: '0 0 auto', mt: 0.125, fontSize: 14, color: tokens.ink.faint }} />
                <span>
                  {t(copyKey('lookFor'))}{' '}
                  <Box component="b" sx={{ color: tokens.ink.primary, fontWeight: 600 }}>
                    {stepCopy('lookFor')}
                  </Box>
                </span>
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>{actions}</Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 1.25,
          rowGap: 1,
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderTop: '1px solid',
          borderColor: tokens.line.hairline,
        }}
      >
        {stepIndex > 0 && (
          <Button
            variant="outlined"
            onClick={() => setStep(CUSTOM_APP_STEP_IDS[stepIndex - 1])}
            sx={{ order: { xs: 2, sm: 1 }, color: tokens.ink.body, borderColor: tokens.line.default, '&:hover': { borderColor: tokens.line.strong, bgcolor: tokens.surface.tint } }}
          >
            {t(copyKey('backStep'))}
          </Button>
        )}
        <Typography
          sx={{
            order: { xs: 1, sm: 2 },
            flex: { xs: '1 0 100%', sm: '0 1 auto' },
            fontSize: 12.5,
            color: saveFailed ? tokens.signal.caution.ink : tokens.ink.muted,
          }}
        >
          {t(copyKey(saveFailed ? 'progressNotSaved' : 'progressSaved'))}
        </Typography>
        <Box sx={{ flex: '1 1 auto', order: 3 }} />
        <Button
          variant="contained"
          disabled={!canNext}
          onClick={onNext}
          sx={{ order: 4, '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' } }}
        >
          {t(copyKey(last ? 'finish' : 'next'))}
        </Button>
      </Box>
    </Box>
  );
};

export default ShopifyCustomAppWizard;
